import React, { useEffect, useState } from "react";
import {
  CreateSchoolInput,
  SchoolData,
  UpdateSchoolInput,
} from "../model/SchoolData";
import "../pages/dashboard/Dashboard.css";

interface EditSchoolModalProps {
  isOpen: boolean;
  school: SchoolData | null;
  onClose: () => void;
  onCreate: (data: CreateSchoolInput) => void;
  onUpdate: (data: UpdateSchoolInput) => void;
}

const emptyForm = {
  name: "",
  address: "",
  phoneNumber: "",
  email: "",
  accountUserName: "",
  accountPassword: "",
  accountPhoneNumber: "",
  accountName: "",
  accountEmail: "",
};

// The backend rejects empty strings on optional fields, so omit them.
const optional = (value: string) => value.trim() || undefined;

const EditSchoolModal: React.FC<EditSchoolModalProps> = ({
  isOpen,
  school,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    setForm({
      ...emptyForm,
      name: school?.name || "",
      address: school?.address || "",
      phoneNumber: school?.phoneNumber || "",
      email: school?.email || "",
    });
  }, [school, isOpen]);

  if (!isOpen) return null;
  const isCreateMode = !school;

  const set =
    (field: keyof typeof emptyForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [field]: e.target.value });

  const canSave =
    !!form.name.trim() &&
    (!isCreateMode ||
      (!!form.accountUserName.trim() &&
        !!form.accountPassword &&
        !!form.accountPhoneNumber.trim()));

  const handleSave = () => {
    if (!canSave) return;
    if (isCreateMode) {
      onCreate({
        name: form.name.trim(),
        address: optional(form.address),
        phoneNumber: optional(form.phoneNumber),
        email: optional(form.email),
        account: {
          userName: form.accountUserName.trim(),
          password: form.accountPassword,
          phoneNumber: form.accountPhoneNumber.trim(),
          name: optional(form.accountName),
          email: optional(form.accountEmail),
        },
      });
    } else {
      onUpdate({
        name: form.name.trim(),
        address: optional(form.address),
        phoneNumber: optional(form.phoneNumber),
        // An empty string clears the email.
        email: form.email.trim(),
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isCreateMode ? "Thêm trường học" : "Sửa trường học"}
          </h2>
          <button onClick={onClose} className="modal-close-button">
            ×
          </button>
        </div>

        <div className="modal-section">
          <h3 className="modal-section-title">Thông tin trường</h3>
          <div className="modal-grid-2">
            <div className="modal-field">
              <label className="modal-label">Tên trường *</label>
              <input
                type="text"
                className="modal-value"
                placeholder="Ví dụ: THCS Tân Văn"
                value={form.name}
                onChange={set("name")}
                autoFocus
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Địa chỉ</label>
              <input
                type="text"
                className="modal-value"
                value={form.address}
                onChange={set("address")}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Số điện thoại</label>
              <input
                type="tel"
                className="modal-value"
                value={form.phoneNumber}
                onChange={set("phoneNumber")}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Email</label>
              <input
                type="email"
                className="modal-value"
                value={form.email}
                onChange={set("email")}
              />
            </div>
          </div>
        </div>

        {isCreateMode && (
          <div className="modal-section">
            <h3 className="modal-section-title">Tài khoản đăng nhập của trường</h3>
            <div className="modal-grid-2">
              <div className="modal-field">
                <label className="modal-label">Tên đăng nhập *</label>
                <input
                  type="text"
                  className="modal-value"
                  value={form.accountUserName}
                  onChange={set("accountUserName")}
                />
              </div>
              <div className="modal-field">
                <label className="modal-label">Mật khẩu *</label>
                <input
                  type="password"
                  className="modal-value"
                  value={form.accountPassword}
                  onChange={set("accountPassword")}
                />
              </div>
              <div className="modal-field">
                <label className="modal-label">Số điện thoại *</label>
                <input
                  type="tel"
                  className="modal-value"
                  value={form.accountPhoneNumber}
                  onChange={set("accountPhoneNumber")}
                />
              </div>
              <div className="modal-field">
                <label className="modal-label">Tên hiển thị</label>
                <input
                  type="text"
                  className="modal-value"
                  value={form.accountName}
                  onChange={set("accountName")}
                />
              </div>
              <div className="modal-field">
                <label className="modal-label">Email</label>
                <input
                  type="email"
                  className="modal-value"
                  value={form.accountEmail}
                  onChange={set("accountEmail")}
                />
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button
            onClick={onClose}
            className="modal-button modal-button-secondary"
          >
            Huỷ
          </button>
          <button
            onClick={handleSave}
            className="modal-button modal-button-primary"
            disabled={!canSave}
          >
            {isCreateMode ? "Tạo" : "Lưu"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSchoolModal;
